// Call Logs query builder: the one place that turns the page's filters, date
// range, page and sort into the server's query string (GET practices/{p}/calls/,
// calls/summary/, calls/export.csv). Dependency-free so Jest can import it
// without the component barrel.

export const PAGE_SIZE_OPTIONS = [10, 25, 50];
export const DEFAULT_PAGE_SIZE = 10;
export const EXPORT_MAX_DAYS = 31;
export const ORDER_NEWEST_FIRST = '-started';
export const ORDER_OLDEST_FIRST = 'started';
export const ORDER_FOLLOW_UP = 'follow_up';

const DAY_MS = 24 * 60 * 60 * 1000;
const SEARCH_MAX = 200;

const iso = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};
const YES_NO = { yes: 'true', no: 'false' };
const yesNo = (value) => YES_NO[value] || '';
const LINKED = { linked: 'true', unlinked: 'false' };

// Filter keys (callLog.filters.js EMPTY_CALL_FILTERS) -> server params.
// `query` is the single search box; it becomes `q`.
export function filtersToParams(filters = {}) {
  return {
    q: String(filters.query || '').trim().slice(0, SEARCH_MAX),
    channel: filters.channel || '',
    outcome: filters.outcome || '',
    direction: ['inbound', 'outbound'].includes(filters.direction) ? filters.direction : '',
    purpose: filters.purpose || '',
    line: filters.line || '',
    provider: filters.provider || '',
    site_number: filters.siteNumber || '',
    missed_reason: filters.missedReason || '',
    follow_up_state: filters.followUpState || '',
    linked: LINKED[filters.matching] || '',
    caller: filters.caller || '',
    has_recording: yesNo(filters.hasRecording),
    has_transcript: yesNo(filters.hasTranscript),
    // Test (synthetic) calls are hidden unless the user ticks "Show".
    include_test: filters.includeTest === 'yes' ? 'true' : 'false',
  };
}

// "Needs follow-up" is its own server order (overdue first, then oldest);
// otherwise the Date column decides.
export function effectiveOrdering(filters = {}, dateOrder = ORDER_NEWEST_FIRST) {
  if (filters.followUpState === 'open') return ORDER_FOLLOW_UP;
  return dateOrder === ORDER_OLDEST_FIRST ? ORDER_OLDEST_FIRST : ORDER_NEWEST_FIRST;
}

export function toQueryString(params) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

// Filters + date range, shared by the list, the summary and the export.
export function filterParams({ filters, dateRange }) {
  return { start: iso(dateRange?.startDate), end: iso(dateRange?.endDate), ...filtersToParams(filters) };
}

// `page` is 0-based here (DataGrid); the server's is 1-based.
export function listParams({ filters, dateRange, page = 0, pageSize = DEFAULT_PAGE_SIZE, ordering = ORDER_NEWEST_FIRST }) {
  return { page_mode: 'fast', page: page + 1, page_size: pageSize, ordering, ...filterParams({ filters, dateRange }) };
}

export const summaryParams = ({ filters, dateRange }) => filterParams({ filters, dateRange });

// The KPI cards and the missed/follow-up chips act as filter toggles, so their
// counts ignore the filters they toggle (otherwise picking "Missed" would zero
// the Answered card). Everything else still applies.
// caller / hasRecording: the New callers and Has recording cards (UX-C).
export const TILE_TOGGLE_FILTERS = ['outcome', 'direction', 'missedReason', 'followUpState', 'caller', 'hasRecording'];
export function tileFilters(filters = {}) {
  const next = { ...filters };
  TILE_TOGGLE_FILTERS.forEach((key) => { next[key] = ''; });
  return next;
}

export function exportParams({ filters, dateRange, ordering }) {
  return { ordering: ordering || ORDER_NEWEST_FIRST, ...filterParams({ filters, dateRange }) };
}

// Mirrors the server's rule (call_log_views.export_csv): a missing bound, or a
// range longer than 31 days, is refused. The UI shows a message instead of
// sending the request.
export function exportRangeTooLong(dateRange) {
  const start = new Date(dateRange?.startDate);
  const end = new Date(dateRange?.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;
  return end.getTime() - start.getTime() > EXPORT_MAX_DAYS * DAY_MS;
}

export const callsBase = (practiceId) => `/api/v1/practices/${practiceId}/calls/`;
export const listUrl = (practiceId, params) => `${callsBase(practiceId)}?${toQueryString(params)}`;
export const summaryUrl = (practiceId, params) => `${callsBase(practiceId)}summary/?${toQueryString(params)}`;
export const exportUrl = (practiceId, params) => `${callsBase(practiceId)}export.csv?${toQueryString(params)}`;
export const detailUrl = (practiceId, callId) => `${callsBase(practiceId)}${encodeURIComponent(callId)}/`;

// Stable identity of "which set of rows is this": every filter, the date range
// and the practice, but not the page, page size or sort. A change here means a
// new result set, so the page goes back to 1.
export const filterSignature = ({ practiceId, filters, dateRange }) => JSON.stringify([practiceId, toQueryString(filterParams({ filters, dateRange }))]);
